import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@Utils';

import { Options } from '@Models';

interface CustomSelectProps {
  options: Options[];
  value: string | null;
  onChange: (option: Options) => void;
  placeholder: string;
}

const CustomSelect = ({ options = [], value = null, onChange, placeholder }: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSelected = (optionValue) => value === optionValue;

  const handleOptionClick = (option: Options) => {
    if (isSelected(option.value)) onChange({ label: '', value: '' });
    else onChange(option);

    setIsOpen(false);
  };

  const handleToggle = () => setIsOpen((prev) => !prev);

  const orderedOptions = useMemo(() => {
    if (!value) return options;

    const selectedOption = options.find((opt) => opt.value === value);
    if (!selectedOption) return options;

    return [selectedOption, ...options.filter((opt) => opt.value !== value)];
  }, [options, value]);

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Select Button */}
      <div
        onClick={handleToggle}
        className="border-solid grow border-[1px] border-gray-200 rounded-lg px-3 min-h-8 py-2 flex justify-between items-center gap-8 cursor-pointer">
        <span className={cn('truncate', !value && 'text-gray-600')}>
          {value ? options.find((opt) => opt.value === value)?.label : placeholder}
        </span>
        <ChevronDown
          className={`size-4 text-gray-900 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border-[1px] border-solid border-gray-200 rounded-lg shadow-lg max-h-60">
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto p-1">
            {orderedOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleOptionClick(option)}
                className={cn(
                  'px-3 py-2 text-left bg-white rounded-md hover:bg-brand-50/70 flex items-center justify-between',
                  'transition-colors duration-150',
                  isSelected(option.value) ? 'bg-brand-50 text-brand-900' : 'text-gray-900',
                )}>
                <div className="font-medium">{option.label}</div>

                {isSelected(option.value) && <Check className="size-4 text-brand-900" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
